import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils-format";
import WhatsAppCTA from "@/components/WhatsAppCTA";

export default function ProductGrid({ limit, filter = "ix" }) {
    const [products, setProducts] = useState([]);
    const [settings, setSettings] = useState({ whatsapp_number: "919971925446" });

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
        api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    // Filter: default to IX-only (site is IX-branded), and pick the 5 core tiers.
    const CORE_IX_ORDER = [
        "signia-styletto-1ix",
        "kit-styletto-1ix",
        "kit-styletto-2ix",
        "kit-styletto-3ix",
        "kit-styletto-5ix",
        "kit-styletto-7ix",
    ];
    let filtered = products;
    if (filter === "ix") {
        // Prefer canonical core-IX order first, then any remaining IX SKU.
        const bySlug = Object.fromEntries(products.map((p) => [p.slug, p]));
        const ordered = CORE_IX_ORDER.map((s) => bySlug[s]).filter(Boolean);
        const rest = products.filter(
            (p) => /ix/i.test(p.slug) && !CORE_IX_ORDER.includes(p.slug) && !/cros/i.test(p.slug),
        );
        filtered = [...ordered, ...rest];
    }
    const list = limit ? filtered.slice(0, limit) : filtered;

    return (
        <section className="section" data-testid="product-grid-section">
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <div className="max-w-[640px]">
                        <p className="eyebrow">The Styletto IX Range</p>
                        <h2 className="font-serif text-[40px] md:text-[56px] leading-[1.04] mt-3">
                            Choose the Styletto IX <span className="italic text-[#003E4D]">made for you.</span>
                        </h2>
                        <p className="mt-5 text-[16px] text-[#4a5568] max-w-[520px] leading-relaxed">
                            Every model on this page is sourced from HearClear&apos;s Signia Certified Partner
                            inventory — genuine devices, full warranty, expert fitting included.
                        </p>
                    </div>
                    <Link to="/products" className="btn-secondary self-start" data-testid="view-all-products-link">
                        View all models <ArrowRight size={16} />
                    </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {list.map((p) => (
                        <article key={p.slug} className="card-premium p-5 flex flex-col" data-testid={`product-card-${p.slug}`}>
                            <Link to={`/products/${p.slug}`} className="block aspect-square bg-[#f3f4f6] rounded-2xl overflow-hidden relative group">
                                <img
                                    src={p.image}
                                    alt={p.name}
                                    className="w-full h-full object-contain p-4 group-hover:scale-[1.04] transition-transform duration-500"
                                    loading="lazy"
                                />
                                {p.badge && (
                                    <span className="absolute top-3 left-3 bg-[#003E4D] text-white text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
                                        {p.badge}
                                    </span>
                                )}
                            </Link>
                            <div className="mt-5 flex-1 flex flex-col">
                                <p className="eyebrow text-[10px]">{p.technology_level}</p>
                                <h3 className="font-serif text-[22px] md:text-[24px] mt-2 leading-tight text-[#1a1a1a]">
                                    {p.short_name || p.name}
                                </h3>
                                <p className="text-[13.5px] text-[#4a5568] mt-2 line-clamp-2 leading-relaxed">
                                    {p.description}
                                </p>
                                <div className="mt-5 flex items-end justify-between">
                                    <div>
                                        <p className="text-[20px] font-semibold text-[#1a1a1a]">{formatINR(p.price)}</p>
                                        {p.emi_per_month && (
                                            <p className="text-[12px] text-[#4a5568] mt-0.5">No-Cost EMI from {formatINR(p.emi_per_month)}/mo</p>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-5 grid grid-cols-2 gap-2">
                                    <WhatsAppCTA
                                        waNumber={settings.whatsapp_number}
                                        message={`Hi, I want the best price for ${p.short_name || p.name}.`}
                                        productSlug={p.slug}
                                        productName={p.short_name || p.name}
                                        sourcePage="product-grid"
                                        className="btn-whatsapp !py-2.5 !px-3 !text-[13px] justify-center"
                                        data-testid={`product-whatsapp-${p.slug}`}
                                    >
                                        <MessageCircle size={14} /> WhatsApp
                                    </WhatsAppCTA>
                                    <Link
                                        to={`/products/${p.slug}`}
                                        className="btn-primary !py-2.5 !px-3 !text-[13px] justify-center"
                                        data-testid={`product-view-${p.slug}`}
                                    >
                                        View Details
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
